import { useEffect } from 'react';
import { observer } from 'mobx-react';
import { Input, Empty, Alert, Spin, Typography, Tooltip } from 'antd';
import { ApartmentOutlined, RightOutlined, RobotOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import { SectionCard } from '@/components/SectionCard';
import { StatusPill } from '@/components/StatusPill';
import { PageHeader } from '@/components/PageHeader';
import { executorPalette } from './executors';
import { workflowSourceView, stepTone, workflowHref } from './workflowSource';
import type { WorkflowCatalogEntry, WorkflowCatalogStep } from '@/api/types';

const { Text } = Typography;

// Workflows = what actually RUNS for the selected merchant (GET /api/AIProcessLibrary/Catalog): database rows
// and workflows defined only in code, each drawn from its effective steps - never from a row's raw JSON, which
// for a code-first workflow is empty ("No steps defined" while the code ran). Each card shows the chain, the
// agent on agent steps, and where the steps come from.

export const WorkflowsListPage = observer(function WorkflowsListPage() {
  const { processLibraryStore: store, merchantContextStore: ctx, themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';

  useEffect(() => {
    void store.loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.selectedMerchantId]);

  const workflows = store.filtered();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingBottom: 32 }}>
      <PageHeader
        icon={<ApartmentOutlined />}
        accent="violet"
        title="Workflows"
        subtitle="Every multi-step orchestration that can run for this practice, defined in code or overridden in the database, shown as it actually runs: its steps, the agent on each agent step, and where the steps come from."
        helpTopic="workflows.overview"
        countLabel={
          store.workflows.length > 0
            ? `${store.workflows.length} workflow${store.workflows.length === 1 ? '' : 's'}`
            : undefined
        }
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <Input.Search
          placeholder="Search workflows by title or name…"
          allowClear
          value={store.searchTerm}
          onChange={(e) => store.setSearchTerm(e.target.value)}
          style={{ maxWidth: 360 }}
        />
        <Text type="secondary" style={{ fontSize: 12 }}>
          {workflows.length === store.workflows.length
            ? `${workflows.length} shown`
            : `${workflows.length} of ${store.workflows.length} match`}
        </Text>
      </div>

      {store.listError && (
        <Alert type="error" message="Failed to load workflows" description={store.listError} showIcon closable />
      )}

      {store.isLoadingList && store.workflows.length === 0 && (
        <SectionCard>
          <div style={{ minHeight: 160, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Spin />
          </div>
        </SectionCard>
      )}

      {!store.isLoadingList && workflows.length === 0 && !store.listError && (
        <SectionCard>
          <Empty description="No workflows found for this merchant" />
        </SectionCard>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))',
          gap: 14,
        }}
      >
        {workflows.map((w) => (
          <WorkflowCard key={w.name} workflow={w} isDark={isDark} />
        ))}
      </div>
    </div>
  );
});

const WorkflowCard = observer(function WorkflowCard({
  workflow,
  isDark,
}: {
  workflow: WorkflowCatalogEntry;
  isDark: boolean;
}) {
  const steps = workflow.steps ?? [];
  const agentNames = Array.from(new Set(workflow.agents.map((a) => a.name).filter((n): n is string => !!n)));
  const source = workflowSourceView(workflow.source);
  const scope = workflow.id == null ? 'code only' : workflow.merchantId == null ? 'global row' : 'this practice';

  const cardBg = isDark ? colors.slate[800] : '#FFFFFF';
  const cardBorder = isDark ? colors.slate[700] : colors.slate[200];

  return (
    <Link to={workflowHref(workflow)} style={{ textDecoration: 'none' }}>
      <div
        style={{
          background: cardBg,
          border: `1px solid ${cardBorder}`,
          borderRadius: 12,
          padding: 16,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          cursor: 'pointer',
          transition: 'transform 100ms ease, border-color 100ms ease, box-shadow 100ms ease',
          height: '100%',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = colors.violet[isDark ? 500 : 600];
          e.currentTarget.style.transform = 'translateY(-1px)';
          e.currentTarget.style.boxShadow = isDark
            ? '0 6px 16px rgba(0,0,0,0.4)'
            : '0 6px 16px rgba(15,23,42,0.06)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = cardBorder;
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        {/* Header — title + scope chip */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: isDark ? colors.slate[50] : colors.slate[900],
                lineHeight: 1.3,
                wordBreak: 'break-word',
              }}
            >
              {workflow.title || workflow.name}
            </div>
            <div
              style={{
                fontSize: 11,
                color: isDark ? colors.slate[400] : colors.slate[500],
                fontFamily: "'JetBrains Mono', monospace",
                marginTop: 3,
                wordBreak: 'break-all',
              }}
            >
              {workflow.name}
            </div>
          </div>
          <Tooltip title={source.explain}>
            <span>
              <StatusPill tone={source.tone}>{source.label}</StatusPill>
            </span>
          </Tooltip>
        </div>

        {/* Executor chain preview — the workflow's "shape" */}
        {workflow.description && (
          <div style={{ fontSize: 12, color: isDark ? colors.slate[300] : colors.slate[600], lineHeight: 1.45 }}>
            {workflow.description}
          </div>
        )}
        <ChainPreview steps={steps} isDark={isDark} />
        {agentNames.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {agentNames.map((n) => (
              <StatusPill key={n} tone="violet" icon={<RobotOutlined />}>{n}</StatusPill>
            ))}
          </div>
        )}

        {/* Footer — counts + chevron */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            marginTop: 'auto',
            paddingTop: 4,
            fontSize: 12,
            color: isDark ? colors.slate[400] : colors.slate[500],
          }}
        >
          <span>
            {steps.length} step{steps.length === 1 ? '' : 's'}
            {agentNames.length > 0 && <> · {agentNames.length} agent{agentNames.length === 1 ? '' : 's'}</>}
            {workflow.modelTitle && <> · {workflow.modelTitle}</>}
            <> · {scope}</>
          </span>
          <RightOutlined style={{ fontSize: 11 }} />
        </div>
      </div>
    </Link>
  );
});

function ChainPreview({ steps, isDark }: { steps: WorkflowCatalogStep[]; isDark: boolean }) {
  if (steps.length === 0) {
    return (
      <div
        style={{
          fontSize: 12,
          color: isDark ? colors.slate[500] : colors.slate[400],
          fontStyle: 'italic',
          padding: '8px 0',
        }}
      >
        No steps defined
      </div>
    );
  }
  // Show up to 4 steps inline. If more, append a "+N" pill.
  const visible = steps.slice(0, 4);
  const overflow = steps.length - visible.length;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
      {visible.map((step, i) => {
        const palette = executorPalette(stepTone(step.category, step.isAgent), isDark);
        const label = step.isAgent ? 'Agent turn' : step.title;
        const tip = step.isAgent
          ? [step.agentName ?? step.agentRefId, step.agentModel].filter(Boolean).join(' · ')
          : step.description ?? step.title;
        return (
          <span key={step.id ?? i} style={{ display: 'inline-flex', alignItems: 'center' }}>
            <Tooltip title={tip} placement="top">
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  padding: '3px 8px',
                  borderRadius: 999,
                  background: palette.bg,
                  border: `1px solid ${palette.border}`,
                  color: palette.fg,
                  fontSize: 11,
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  maxWidth: 180,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {step.isAgent && <RobotOutlined style={{ fontSize: 11 }} />}
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
              </span>
            </Tooltip>
            {i < visible.length - 1 && (
              <RightOutlined style={{ fontSize: 9, color: isDark ? colors.slate[500] : colors.slate[400], margin: '0 2px' }} />
            )}
          </span>
        );
      })}
      {overflow > 0 && (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '3px 8px',
            borderRadius: 999,
            background: isDark ? colors.slate[700] : colors.slate[100],
            color: isDark ? colors.slate[300] : colors.slate[500],
            fontSize: 11,
            fontWeight: 600,
          }}
        >
          +{overflow} more
        </span>
      )}
    </div>
  );
}
